import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { RowIndicatorVariant } from '../data-popper';
import { ChartElementTooltipConfig } from './_types';

type MarkerStyles = {
  color?: string;
  opacity?: number | string;
};

type ElementTooltipConfig<T> = {
  indicatorVariant?: RowIndicatorVariant;
  tooltipConfig?: ChartElementTooltipConfig;
  getMarkerStylesByPoint?(dataPoint: T): MarkerStyles | undefined;
};

type TooltipConfigState<T> = {
  setElementConfig(elementId: string, config: ElementTooltipConfig<T>): void;
  getElementConfig(elementId: string): ElementTooltipConfig<T> | undefined;
};

const ProviderContext = createContext<TooltipConfigState<unknown>>({} as never);

export const TooltipConfigProvider = <T,>({ children }: PropsWithChildren) => {
  const [elementConfigs, setElementConfigs] = useState<
    Record<string, ElementTooltipConfig<T>>
  >({});

  const setElementConfig = useCallback(
    (elementId: string, config: ElementTooltipConfig<T>) =>
      setElementConfigs((prev) => ({ ...prev, [elementId]: config })),
    []
  );

  const getElementConfig = useCallback(
    (elementId: string) => elementConfigs[elementId],
    [elementConfigs]
  );

  return (
    <ProviderContext.Provider
      value={{
        setElementConfig,
        getElementConfig,
      }}
    >
      {children}
    </ProviderContext.Provider>
  );
};

export const useTooltipConfig = <T,>(): TooltipConfigState<T> => {
  return useContext(ProviderContext);
};
